import type {
  BiomapExplorerPopupType,
  BiomapMsaPressureCode,
  IBiomapExplorerKpiSeries,
  IBiomapHabitatExplorerKpiData,
  IBiomapHabitatSubdivisionKpi,
  IBiomapHabitatYearKpi,
  IBiomapMsaBandKpi,
  IBiomapMsaBufferBandKpi,
  IBiomapMsaComponentKpi,
  IBiomapMsaExplorerKpiData,
  IBiomapMsaHighValueAreaKpi,
  IBiomapMsaHistogramBin,
  IBiomapMsaLinearPointKpi,
  IBiomapMsaLinearSegmentKpi,
  IBiomapMsaSubdivisionKpi,
  IBiomapMsaYearKpi,
  IBiomapVectorKpiPayload,
} from '../../interfaces/biomap/IBiomapExplorerKpi';
import type { BiomapProduct } from '../../interfaces/biomap/IBiomapLayer';
import type { IVectorKpiPayload } from '../../interfaces/sjmap2/vector/IVectorKpi';

const POPUP_TYPE_BY_PRODUCT: Record<BiomapProduct, BiomapExplorerPopupType> = {
  habitat: 'habitat_mapping',
  msa: 'terrestrial_msa',
};

/**
 * `biomap_msa` was used in the BIOMAP PMTiles contract examples before the
 * frontend component key was finalised as `terrestrial_msa`. Keep the alias at
 * this single boundary and normalise it immediately so the rest of the UI only
 * deals with one popup type.
 */
const ACCEPTED_POPUP_TYPES_BY_PRODUCT: Record<BiomapProduct, readonly string[]> = {
  habitat: ['habitat_mapping'],
  msa: ['terrestrial_msa', 'biomap_msa'],
};

const MSA_PRESSURE_CODES = new Set<BiomapMsaPressureCode>([
  'land_use',
  'climate',
  'nitrogen',
  'infra_disturbance',
  'fragmentation',
]);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const isOptionalFiniteNumber = (value: unknown): boolean =>
  value === undefined || isFiniteNumber(value);

const isOptionalString = (value: unknown): boolean =>
  value === undefined || value === null || typeof value === 'string';

const isString = (value: unknown): value is string => typeof value === 'string';

const isNumberRecord = (value: unknown): value is Record<string, number> =>
  isRecord(value) && Object.values(value).every(isFiniteNumber);

const isOptionalNumberRecord = (value: unknown): boolean =>
  value === undefined || isNumberRecord(value);

const isOptionalArray = <T>(
  value: unknown,
  guard: (item: unknown) => item is T,
): value is T[] | undefined =>
  value === undefined || (Array.isArray(value) && value.every(guard));

const isHabitatSubdivision = (
  value: unknown,
): value is IBiomapHabitatSubdivisionKpi =>
  isRecord(value)
  && isString(value['id'])
  && isString(value['name'])
  && isFiniteNumber(value['area_ha'])
  && isNumberRecord(value['area_by_class_ha'])
  && isFiniteNumber(value['natural_share_pct']);

const isHabitatYear = (value: unknown): value is IBiomapHabitatYearKpi =>
  isRecord(value)
  && isFiniteNumber(value['year'])
  && isOptionalString(value['scenario'])
  && isOptionalNumberRecord(value['area_by_class_ha'])
  && isOptionalFiniteNumber(value['natural_share_pct'])
  && isOptionalFiniteNumber(value['shannon_index'])
  && isOptionalFiniteNumber(value['patch_count'])
  && isOptionalFiniteNumber(value['mean_patch_area_ha'])
  && isOptionalArray(value['subdivisions'], isHabitatSubdivision);

const isMsaHistogramBin = (value: unknown): value is IBiomapMsaHistogramBin =>
  isRecord(value)
  && isFiniteNumber(value['bin_min'])
  && isFiniteNumber(value['bin_max'])
  && isFiniteNumber(value['area_ha']);

const isMsaComponent = (value: unknown): value is IBiomapMsaComponentKpi =>
  isRecord(value)
  && isString(value['code'])
  && MSA_PRESSURE_CODES.has(value['code'] as BiomapMsaPressureCode)
  && isString(value['label'])
  && isFiniteNumber(value['mean_value']);

const isMsaSubdivision = (value: unknown): value is IBiomapMsaSubdivisionKpi => {
  if (
    !isRecord(value)
    || !isString(value['id'])
    || !isString(value['name'])
    || !isFiniteNumber(value['msa_mean'])
    || !isRecord(value['components'])
  ) {
    return false;
  }

  return Object.entries(value['components']).every(
    ([code, componentValue]) =>
      MSA_PRESSURE_CODES.has(code as BiomapMsaPressureCode)
      && isFiniteNumber(componentValue),
  );
};

const isMsaBand = (value: unknown): value is IBiomapMsaBandKpi =>
  isRecord(value)
  && isString(value['label'])
  && isFiniteNumber(value['area_ha'])
  && isFiniteNumber(value['share_pct']);

const isPosition = (value: unknown): value is number[] =>
  Array.isArray(value)
  && value.length >= 2
  && value.every(isFiniteNumber);

const isLinearRing = (value: unknown): value is number[][] =>
  Array.isArray(value)
  && value.length >= 4
  && value.every(isPosition);

const isPolygonCoordinates = (value: unknown): value is number[][][] =>
  Array.isArray(value)
  && value.length >= 1
  && value.every(isLinearRing);

const isMultiPolygonCoordinates = (value: unknown): value is number[][][][] =>
  Array.isArray(value)
  && value.length >= 1
  && value.every(isPolygonCoordinates);

const isMsaHighValueGeometry = (value: unknown): boolean => {
  if (!isRecord(value) || !isString(value['type'])) {
    return false;
  }

  if (value['type'] === 'Polygon') {
    return isPolygonCoordinates(value['coordinates']);
  }

  if (value['type'] === 'MultiPolygon') {
    return isMultiPolygonCoordinates(value['coordinates']);
  }

  return false;
};

const isMsaHighValueArea = (
  value: unknown,
): value is IBiomapMsaHighValueAreaKpi =>
  isRecord(value)
  && isString(value['id'])
  && isFiniteNumber(value['area_ha'])
  && isFiniteNumber(value['mean_msa'])
  && (value['geometry'] === undefined || isMsaHighValueGeometry(value['geometry']));

const isMsaLinearPoint = (value: unknown): value is IBiomapMsaLinearPointKpi =>
  isRecord(value)
  && isFiniteNumber(value['progressive_m'])
  && isFiniteNumber(value['mean_msa']);

const isMsaLinearSegment = (
  value: unknown,
): value is IBiomapMsaLinearSegmentKpi =>
  isRecord(value)
  && isFiniteNumber(value['progressive_start_m'])
  && isFiniteNumber(value['progressive_end_m'])
  && isFiniteNumber(value['mean_msa']);

const isMsaBufferBand = (value: unknown): value is IBiomapMsaBufferBandKpi =>
  isRecord(value)
  && isFiniteNumber(value['distance_m'])
  && isFiniteNumber(value['mean_msa']);

const isMsaYear = (value: unknown): value is IBiomapMsaYearKpi =>
  isRecord(value)
  && isFiniteNumber(value['year'])
  && isOptionalString(value['scenario'])
  && isOptionalFiniteNumber(value['msa_mean'])
  && isOptionalArray(value['histogram'], isMsaHistogramBin)
  && isOptionalFiniteNumber(value['std_dev'])
  && isOptionalArray(value['distribution'], isMsaBand)
  && isOptionalArray(value['components'], isMsaComponent)
  && isOptionalString(value['dominant_pressure'])
  && isOptionalArray(value['subdivisions'], isMsaSubdivision)
  && isOptionalFiniteNumber(value['threshold'])
  && isOptionalArray(value['areas'], isMsaHighValueArea)
  && isOptionalArray(value['buffer_profile'], isMsaBufferBand)
  && isOptionalFiniteNumber(value['sample_interval_m'])
  && isOptionalArray(value['points'], isMsaLinearPoint)
  && isOptionalArray(value['segments'], isMsaLinearSegment);

const hasYearSeries = <TYear extends { year: number }>(
  value: unknown,
  yearGuard: (item: unknown) => item is TYear,
): value is IBiomapExplorerKpiSeries<TYear> =>
  isRecord(value)
  && Array.isArray(value['years'])
  && value['years'].every(yearGuard);

export const isBiomapHabitatExplorerKpiData = (
  value: unknown,
): value is IBiomapHabitatExplorerKpiData =>
  hasYearSeries(value, isHabitatYear);

export const isBiomapMsaExplorerKpiData = (
  value: unknown,
): value is IBiomapMsaExplorerKpiData =>
  hasYearSeries(value, isMsaYear);

export const parseBiomapVectorKpiPayload = (
  payload: IVectorKpiPayload,
  expectedProduct: BiomapProduct,
): IBiomapVectorKpiPayload => {
  const expectedPopupType = POPUP_TYPE_BY_PRODUCT[expectedProduct];
  const receivedPopupType = payload.popup_type;

  if (
    !receivedPopupType
    || !ACCEPTED_POPUP_TYPES_BY_PRODUCT[expectedProduct].includes(receivedPopupType)
  ) {
    throw new Error(
      `Unexpected BIOMAP popup type: expected ${expectedPopupType}, received ${receivedPopupType ?? 'null'}.`,
    );
  }

  const hasValidData = expectedProduct === 'habitat'
    ? isBiomapHabitatExplorerKpiData(payload.popup_data)
    : isBiomapMsaExplorerKpiData(payload.popup_data);

  if (!hasValidData) {
    throw new Error(
      `Invalid BIOMAP ${expectedProduct} KPI payload: popup_data.years has an invalid structure.`,
    );
  }

  return {
    ...payload,
    popup_type: expectedPopupType,
    popup_data: payload.popup_data,
  } as IBiomapVectorKpiPayload;
};

export const resolvePreferredBiomapYear = (
  referenceDate?: string | Date | null,
): number | undefined => {
  if (!referenceDate) {
    return undefined;
  }

  const date = referenceDate instanceof Date
    ? referenceDate
    : new Date(referenceDate);

  if (Number.isNaN(date.getTime())) {
    return undefined;
  }

  return date.getUTCFullYear();
};
